import BenchlingConfig from 'BenchlingConfig';
import * as Metrics from 'metrics/Metrics';
import * as Logging from 'util/Logging';

/**
 * Imperatively builds and shows a Google Drive Picker using the RAW Google
 * Picker JS API (`gapi.load('picker')` + `google.picker.PickerBuilder`).
 *
 * The raw API is used deliberately rather than a web-component wrapper: wrappers
 * accept the OAuth access token as a DOM attribute, which is interceptable by
 * anything with DOM access. Here the access token lives in a JS CLOSURE ONLY — it
 * is passed straight into `PickerBuilder.setOAuthToken` and is never mounted on
 * the DOM, never written to localStorage, and never logged.
 *
 * VERSION PINNING (BNCH-158454, epic BNCH-158362 step 2): Google does NOT
 * support version-pinning the Picker JS library. `gapi.load('picker')` fetches a
 * rolling, unversioned bundle from `apis.google.com` — there is no version
 * parameter and no stable SRI-able URL to pin against. Our mitigation is
 * therefore defensive: feature-detect the API surface we rely on after load, and
 * monitor real load/mount failures (see the metrics below) rather than trusting
 * that a given API shape will always be served.
 */

// Loader for the top-level gapi bootstrap. `google.picker` only types the
// `google.picker` namespace; `@types/gapi` provides `gapi.load`.
const GAPI_SCRIPT_SRC = 'https://apis.google.com/js/api.js';

// Google renders the picker inside an iframe once `setVisible(true)` runs. We
// poll briefly for it so we can detect silent mount failures.
//
// WARNING: This selector is brittle. The Picker library is unversioned (see the
// version-pinning note above), so Google may change the iframe's class or src at
// any time. If `iframe_not_mounted` starts firing spuriously, re-inspect the
// rendered DOM and update this selector.
const PICKER_IFRAME_SELECTOR = 'iframe[class^="picker"][src*="google.com/picker"]';
const IFRAME_MOUNT_CHECK_MAX_ATTEMPTS = 10;
const IFRAME_MOUNT_CHECK_INTERVAL_MS = 50;

// gapi.load fetches the (unversioned) Picker bundle over the network. If the
// loader stalls without calling either callback or onerror, the memoized promise
// would hang forever for the whole page session. Bound it so a stall resolves
// {loaded: false} with telemetry (and lets a later attempt retry). 10s is a
// generous ceiling for a script that normally loads in well under a second.
const GAPI_LOAD_TIMEOUT_MS = 10000;

export interface BuildGoogleDrivePickerOptions {
  // Kept in a closure only; never stored or DOM-mounted.
  accessToken: string;
  multiselect: boolean;
  onPicked: (docs: Array<google.picker.DocumentObject>) => void;
  onCanceled: () => void;
  // Invoked when the Picker reports an internal error (Action.ERROR). Optional
  // so existing callers are unaffected; lets a caller recover/dispose on error.
  onError?: () => void;
}

export interface GoogleDrivePickerHandle {
  dispose: () => void;
}

interface PickerApiLoadResult {
  loaded: boolean;
  // Populated only on failure; stringified before logging so tokens/file data
  // are never emitted. Every failure path constructs an Error, so this is
  // always an Error when present.
  error?: Error;
}

// Memoized so the loader script is injected at most once per page session and
// concurrent callers share the same in-flight load. Reset to null after a failed
// load (see buildAndShowGoogleDrivePicker) so a subsequent attempt can retry. The
// promise never rejects — failures resolve with `{loaded: false}`.
let pickerApiLoadPromise: Promise<PickerApiLoadResult> | null = null;

function loadPickerApi(): Promise<PickerApiLoadResult> {
  if (pickerApiLoadPromise != null) {
    return pickerApiLoadPromise;
  }

  pickerApiLoadPromise = new Promise<PickerApiLoadResult>((resolve) => {
    // Any <script> we inject so we can remove it on failure (see below). Only
    // set when gapi is not already present.
    let injectedScript: HTMLScriptElement | null = null;

    // Single failure path shared by every failure mode (script error, missing
    // gapi, missing Picker module, or a served bundle missing members we rely
    // on). Logging the failure HERE — rather than in each caller continuation —
    // dedupes the LOG: N concurrent callers share this one promise, so one
    // underlying failure is logged (and Sentry-reported) exactly once. The
    // picker_api_not_loaded METRIC is deliberately NOT emitted here; it is
    // emitted per caller in buildAndShowGoogleDrivePicker so it shares the same
    // per-invocation denominator as the other failure metrics (a failed load
    // shared by N callers = N failed invocations). We also remove the injected
    // <script> so sequential failed retries don't pile up orphaned tags. The
    // memo itself is reset by the caller (see buildAndShowGoogleDrivePicker) to
    // keep reset off the synchronous executor path.
    const fail = (reason: PickerApiLoadFailureReason, error: Error): void => {
      injectedScript?.remove();
      logPickerApiLoadFailure(reason, error);
      resolve({loaded: false, error});
    };

    const loadPickerModule = (): void => {
      window.gapi.load('picker', {
        callback: () => {
          // Fold feature-detection into the memoized load so a bundle that
          // loads but is missing required members resolves {loaded: false}
          // (triggering the same reset/retry policy as an outright load
          // failure) rather than memoizing {loaded: true} forever.
          if (hasRequiredPickerApi()) {
            resolve({loaded: true});
          } else {
            fail(
              'missing_picker_members',
              new Error('gapi.load("picker") completed but the Picker API is missing required members')
            );
          }
        },
        onerror: () => {
          fail(
            'picker_module_load_failed',
            new Error('gapi.load("picker") failed to load the Picker module')
          );
        },
        // Bound the load so a stalled loader resolves {loaded: false} (with the
        // shared failure telemetry) rather than hanging the memo forever.
        timeout: GAPI_LOAD_TIMEOUT_MS,
        ontimeout: () => {
          fail('picker_module_load_timeout', new Error('gapi.load("picker") timed out'));
        },
      });
    };

    // Guard `gapi.load` is callable, not merely that `gapi` is defined:
    // `typeof null !== 'undefined'` is true, and a malformed bundle may leave
    // window.gapi set without a usable `.load`. Route the bad-shape case through
    // fail() so no synchronous throw can escape this Promise executor (which
    // would reject the promise, violating the never-rejects contract above).
    if (typeof window.gapi?.load === 'function') {
      loadPickerModule();
      return;
    }
    if (typeof window.gapi !== 'undefined') {
      fail('gapi_load_not_callable', new Error('window.gapi is present but gapi.load is unavailable'));
      return;
    }

    // Follows the createElement/appendChild loader pattern in
    // client/llm/chat-library/auth/GoogleTagManagerForAITrial.ts.
    const script = document.createElement('script');
    injectedScript = script;
    script.src = GAPI_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      // Same callable-gapi.load guard as the already-present branch above: a
      // 200 from api.js (e.g. a CSP/adblock stub) does not guarantee a usable
      // gapi.load. Route the unavailable case through fail() rather than hanging.
      if (typeof window.gapi?.load === 'function') {
        loadPickerModule();
      } else {
        fail('script_loaded_gapi_not_callable', new Error('window.gapi loaded but gapi.load is unavailable'));
      }
    };
    script.onerror = () => {
      fail('script_load_error', new Error('Failed to load the GAPI script (api.js)'));
    };
    // Guard against a missing insertion point: if this ever runs before
    // <body> exists, `document.body` is null and appendChild would throw
    // synchronously inside this Promise executor, rejecting the promise and
    // breaking the never-rejects contract above. Fall back to <head>, then
    // <html>, and route a total absence through fail() like every other
    // failure mode.
    const mountPoint = document.body ?? document.head ?? document.documentElement;
    if (mountPoint == null) {
      fail('no_mount_point', new Error('No document element available to load the GAPI script'));
      return;
    }
    mountPoint.appendChild(script);
  });

  return pickerApiLoadPromise;
}

/**
 * Defensive feature-detection of the Picker API surface we depend on. Because
 * the library is unversioned, a served bundle could in principle be missing
 * members we rely on; treat that as a load failure rather than throwing.
 */
function hasRequiredPickerApi(): boolean {
  const picker = window.google?.picker;
  return (
    picker != null &&
    typeof picker.PickerBuilder === 'function' &&
    typeof picker.DocsView === 'function' &&
    picker.Action != null &&
    picker.Feature != null &&
    // DocsViewMode (makeDocsView) and Response (the picker callback) are used
    // after load too, so detect them here as well — otherwise a bundle missing
    // them passes this check and throws at runtime, defeating the defensive
    // "load failure instead of throw" contract.
    picker.DocsViewMode != null &&
    picker.Response != null &&
    // Document (its NAME/ID/MIME_TYPE keys) is dereferenced when reading the
    // picked files in validateAndDownloadGoogleDriveFiles, so a bundle missing
    // it must also fail the load defensively rather than throw at runtime.
    picker.Document != null
  );
}

// Closed set of load-failure reasons recorded in the failure warn log. Keep
// this a finite union (not `string`) so future edits can't record a
// high-cardinality value (e.g. a raw statusText or error message) under this
// dimension. The reason rides in the warn log's `extra`, NOT as a metric tag:
// metric tags are expensive (computationally and in $), and the dimension is
// better carried in the cheaper warn log where an on-call looks after the
// (untagged) picker_api_not_loaded metric fires. This matches the untagged-count
// convention in the sibling ValidateAndDownloadGoogleDriveFiles.ts (PR #244420).
type PickerApiLoadFailureReason =
  | 'missing_picker_members'
  | 'picker_module_load_failed'
  | 'picker_module_load_timeout'
  | 'gapi_load_not_callable'
  | 'script_loaded_gapi_not_callable'
  | 'script_load_error'
  | 'no_mount_point';

// Reasons that indicate a broken assumption rather than a transient/
// environmental failure, so they warrant Sentry visibility (Logging.error).
// `missing_picker_members` means gapi loaded the Picker module but it lacks
// members we depend on — a served-bundle integrity problem Google should never
// ship, and one that keeps the picker permanently broken until fixed.
// `gapi_load_not_callable` and `script_loaded_gapi_not_callable` mean gapi
// loaded (or api.js returned 200) but `gapi.load` isn't a callable function —
// a structural/broken-assumption failure about the served bundle's shape, not
// an environmental one. Every other reason is environmental (adblock/CSP script
// error, network, timeout) and stays at `warn` so we don't flood Sentry with
// noise we can't act on.
const ERROR_LEVEL_LOAD_FAILURE_REASONS: ReadonlySet<PickerApiLoadFailureReason> = new Set([
  'missing_picker_members',
  'gapi_load_not_callable',
  'script_loaded_gapi_not_callable',
]);

// Logs a load failure exactly once per underlying failure (deduped across the
// concurrent callers that share the memoized load promise). The
// picker_api_not_loaded METRIC is emitted separately, per caller, in
// buildAndShowGoogleDrivePicker so it shares the per-invocation denominator; see
// the `fail` comment in loadPickerApi.
function logPickerApiLoadFailure(reason: PickerApiLoadFailureReason, err: Error): void {
  // Stringify the error so we never emit OAuth tokens or file data to logs.
  const log = ERROR_LEVEL_LOAD_FAILURE_REASONS.has(reason) ? Logging.error : Logging.warn;
  log('Google Drive Picker API failed to load', {
    extra: {reason, error: String(err)},
  });
}

// Builds a DocsView with the options shared by every view (LIST mode, folders
// included, folder-selection disabled), optionally enabling shared drives.
// Mirrors UploadDropdownButton.tsx.
function makeDocsView({enableDrives}: {enableDrives: boolean}): google.picker.DocsView {
  const view = new window.google.picker.DocsView()
    .setIncludeFolders(true)
    .setSelectFolderEnabled(false)
    .setMode(window.google.picker.DocsViewMode.LIST);
  if (enableDrives) {
    view.setEnableDrives(true);
  }
  return view;
}

function buildPicker(options: BuildGoogleDrivePickerOptions, appId: string): google.picker.Picker {
  const picker = window.google.picker;

  // My Drive view, then a shared-drive view (same options plus shared drives).
  const myDriveView = makeDocsView({enableDrives: false});
  const sharedDriveView = makeDocsView({enableDrives: true});

  // Google Cloud API key for the Picker (PickerBuilder.setDeveloperKey),
  // restricted to the Picker API and to our HTTP referrers. Not an OAuth client
  // secret — sourced from deploy config via BenchlingConfig. Passed as an empty
  // string when unset so the builder simply omits it (the setter is a no-op on
  // empty per the Picker API).
  const apiKey = BenchlingConfig.ELN_GDRIVE_API_KEY;
  const builder = new picker.PickerBuilder()
    .setOAuthToken(options.accessToken)
    .setDeveloperKey(apiKey)
    .setAppId(appId)
    .addView(myDriveView)
    .addView(sharedDriveView)
    .setCallback((data: google.picker.ResponseObject) => {
      const action = data[picker.Response.ACTION];
      if (action === picker.Action.PICKED) {
        options.onPicked(data.docs ?? []);
      } else if (action === picker.Action.CANCEL) {
        options.onCanceled();
      } else if (action === picker.Action.ERROR) {
        // The Picker reported an internal error. Neither onPicked nor
        // onCanceled applies; surface it via telemetry consistent with the
        // file's monitoring posture, then notify the caller so it can recover.
        Metrics.count('notebook.gdrive_picker.redirect.picker_builder.picker_error', 1);
        Logging.warn('Google Drive Picker reported an error');
        options.onError?.();
      }
    });

  if (options.multiselect) {
    builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
  }

  return builder.build();
}

/**
 * Polls briefly for the picker iframe and emits `iframe_not_mounted` if it
 * never appears. Returns a `cancel()` that stops the poll — callers MUST call
 * it on dispose so a picker torn down within the ~500ms poll window does not
 * spuriously emit the failure metric.
 */
function verifyPickerIframeMounted(): () => void {
  let attemptsRemaining = IFRAME_MOUNT_CHECK_MAX_ATTEMPTS;
  let timeoutId: number | null = null;
  let cancelled = false;
  const check = (): void => {
    if (cancelled || document.querySelector(PICKER_IFRAME_SELECTOR) != null) {
      return;
    }
    attemptsRemaining -= 1;
    if (attemptsRemaining <= 0) {
      Metrics.count('notebook.gdrive_picker.redirect.picker_builder.iframe_not_mounted', 1);
      Logging.error('Google Drive Picker iframe was not mounted after polling');
      return;
    }
    timeoutId = window.setTimeout(check, IFRAME_MOUNT_CHECK_INTERVAL_MS);
  };
  check();
  return () => {
    cancelled = true;
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
    }
  };
}

/**
 * Builds a Google Drive Picker and makes it visible.
 *
 * Returns a {@link GoogleDrivePickerHandle} whose `dispose()` tears the picker
 * down. Returns `null` when the Picker API fails to load or is missing required
 * members — callers should surface a retry affordance rather than crash.
 */
export async function buildAndShowGoogleDrivePicker(
  options: BuildGoogleDrivePickerOptions
): Promise<GoogleDrivePickerHandle | null> {
  // Untagged per-invocation count of how often the picker is asked to build and
  // show. This is the single denominator for every failure metric in this file:
  // each failure metric is emitted at most once per invocation, so
  // failure_metric / build_and_show_google_drive_picker_called is a true rate.
  // picker_api_not_loaded is emitted per caller below (its LOG is deduped in
  // loadPickerApi) precisely so it shares this denominator.
  Metrics.count(
    'notebook.gdrive_picker.redirect.picker_builder.build_and_show_google_drive_picker_called',
    1
  );
  const loadResult = await loadPickerApi();
  if (!loadResult.loaded) {
    // Emit the untagged load-failure metric PER CALLER (the log was already
    // written once inside loadPickerApi, deduped across concurrent callers that
    // share the memoized promise). Emitting per caller keeps this metric aligned
    // with the per-invocation denominator above: a load shared by N callers that
    // fails counts as N failed invocations, not one. Then clear the memo so a
    // subsequent attempt can retry the load.
    Metrics.count('notebook.gdrive_picker.redirect.picker_builder.picker_api_not_loaded', 1);
    pickerApiLoadPromise = null;
    return null;
  }

  // Treat a missing/malformed app id as a load failure, mirroring the menu-item
  // guard in UploadDropdownButton. AUTH_GOAUTH_PROJECT_NUMBER defaults to '' when
  // the deploy config value is unset; building a picker with setAppId('') silently
  // produces a broken picker.
  const appId = BenchlingConfig.AUTH_GOAUTH_PROJECT_NUMBER;
  if (!appId) {
    // This is a static-config bug (empty AUTH_GOAUTH_PROJECT_NUMBER), NOT a
    // transient Google API load failure. Emit a DISTINCT metric so a
    // deploy/config problem isn't conflated with a network failure on the
    // rollout dashboard, and log at error level so Sentry surfaces the
    // misconfiguration (unlike the environmental load failures, this is always
    // actionable on our side).
    Metrics.count('notebook.gdrive_picker.redirect.picker_builder.picker_app_id_missing', 1);
    Logging.error('Google Drive project number (app id) is empty');
    return null;
  }

  const picker = buildPicker(options, appId);
  picker.setVisible(true);
  const cancelIframeMountCheck = verifyPickerIframeMounted();

  return {
    dispose: () => {
      // Stop the mount-check poll first so tearing the picker down within the
      // poll window doesn't spuriously emit iframe_not_mounted.
      cancelIframeMountCheck();
      try {
        picker.dispose();
        // Intentionally swallow: a throw here must not prevent the
        // setVisible(false) fallback. We observe it via telemetry instead.
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (err) {
        Metrics.count('notebook.gdrive_picker.redirect.picker_builder.dispose_threw', 1);
        Logging.warn('Google Drive Picker dispose() threw', {
          extra: {error: String(err)},
        });
        // Fallback visibility guard, reached only when dispose() threw: try to
        // hide the picker so a failed teardown doesn't leave it on screen. The
        // real library can throw here (already-disposed picker); swallow that
        // (with telemetry) rather than let it break disposal.
        try {
          picker.setVisible(false);
          // Intentionally swallow: a throw here (already-disposed picker) must
          // not break disposal. We observe it via telemetry instead.
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (setVisibleErr) {
          // Pair the warn with a metric for parity with every other failure
          // path in this file, so the swallowed throw is observable on the
          // rollout dashboard rather than only in logs.
          Metrics.count(
            'notebook.gdrive_picker.redirect.picker_builder.set_visible_threw_after_dispose_threw',
            1
          );
          Logging.warn('Google Drive Picker setVisible(false) threw after dispose() threw', {
            extra: {error: String(setVisibleErr)},
          });
        }
      }
    },
  };
}

/**
 * Test-only: resets the memoized loader promise so each test starts from a clean
 * load state. Not for production use.
 */
export function resetPickerApiLoadStateForTests(): void {
  pickerApiLoadPromise = null;
}
